import { useState, useCallback } from 'react'
import styled from 'styled-components'
import WorkPlannerSwimlane, { SLOT_WIDTH, LABEL_WIDTH } from './WorkPlannerSwimlane'
import WorkPlannerOpInfoModal from './WorkPlannerOpInfoModal'
import useTranslation from '../../hooks/useTranslation'
import Loader from '../generic/Loader'

const addMinutes = (date, m) => new Date(date.getTime() + m * 60000)

// ── Styled ─────────────────────────────────────────────────────────────────

const BoardWrapper = styled.div`
    flex: 1;
    overflow: auto;
    min-height: 0;
    display: flex;
    flex-direction: column;

    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.area.highBg} transparent;
    &::-webkit-scrollbar { width: 6px; height: 6px; }
    &::-webkit-scrollbar-thumb { background: ${({ theme }) => theme.area.highBg}; border-radius: 3px; }
`

const StickyHeader = styled.div`
    display: flex;
    align-items: stretch;
    border-bottom: 2px solid ${({ theme }) => theme.area.lightBorder};
    background: ${({ theme }) => theme.card.background};
    position: sticky;
    top: 0;
    z-index: 10;
    flex-shrink: 0;
`

const AxisCorner = styled.div`
    width: ${LABEL_WIDTH}px;
    min-width: ${LABEL_WIDTH}px;
    flex-shrink: 0;
    border-right: 1px solid ${({ theme }) => theme.area.lightBorder};
    padding: 0.4rem 1rem;
    font-size: var(--font-XS);
    font-weight: 600;
    color: ${({ theme }) => theme.font.label};
    display: flex;
    align-items: center;
`

const AxisCells = styled.div`
    display: flex;
`

const AxisCell = styled.div`
    width: ${SLOT_WIDTH}px;
    min-width: ${SLOT_WIDTH}px;
    flex-shrink: 0;
    padding: 0.4rem 0 0.4rem ${({ $isHour }) => $isHour ? '3px' : '0'};
    text-align: left;
    font-size: var(--font-XS);
    color: ${({ $isHour, theme }) => $isHour ? theme.font.color : theme.font.label};
    font-weight: ${({ $isHour }) => $isHour ? '600' : '400'};
    border-left: 1px solid ${({ $isHour, $isFirst, theme }) => ($isHour && !$isFirst) ? theme.area.lightBorder : 'transparent'};
    overflow: visible;
    white-space: nowrap;
    pointer-events: none;
`


const EmptyState = styled.div`
    flex: 1;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 3rem;
    font-size: var(--font-M);
    color: ${({ theme }) => theme.font.label};
`

const formatSlotLabel = (slot) => {
    const h = slot.getHours().toString().padStart(2, '0')
    const m = slot.getMinutes()
    if (m === 0)  return `${h}:00`
    if (m === 30) return ':30'
    return ''
}

// ── Component ──────────────────────────────────────────────────────────────

const WorkPlannerBoard = ({
    selectedOp,
    allResources,
    eligibleResourceIds,
    currentOpIds,
    onSelectOp,
    bookings,
    earliestStart,
    shiftStart,
    shiftHours,
    labelConfig,
    bufferHours,
    slotMinutes,
    snapPreview,
    loading,
    opInfoModalConfig,
    resourceEffectiveShifts,
    fieldConfig
}) => {
    const { t } = useTranslation()
    const [infoOp, setInfoOp] = useState(null)
    const handleInfo   = useCallback((op) => setInfoOp(op), [])
    const handleInfoClose = useCallback(() => setInfoOp(null), [])

    // Real shift end (droppable window) — buffer cells beyond this are display-only
    const shiftEnd = addMinutes(shiftStart, shiftHours * 60)

    // Generate time slots including buffer hours for visual overflow
    const totalDisplayHours = shiftHours + (bufferHours ?? 0)
    const slotCount = (totalDisplayHours * 60) / slotMinutes
    const slots = Array.from({ length: slotCount }, (_, i) =>
        addMinutes(shiftStart, i * slotMinutes)
    )

    if (loading && allResources.length === 0) {
        return <BoardWrapper><Loader /></BoardWrapper>
    }

    if (allResources.length === 0 && !loading) {
        return <BoardWrapper><EmptyState>{t(labelConfig?.noResources ?? 'manufacturing.planner.board.noWorkCenters')}</EmptyState></BoardWrapper>
    }

    return (
        <BoardWrapper>
            {/* Time axis header */}
            <StickyHeader>
                <AxisCorner>{t(labelConfig?.resourceColumn ?? 'manufacturing.planner.board.workCenter')}</AxisCorner>
                <AxisCells>
                    {slots.map((slot, i) => (
                        <AxisCell key={slot.getTime()} $isHour={slot.getMinutes() === 0} $isFirst={i === 0}>
                            {formatSlotLabel(slot)}
                        </AxisCell>
                    ))}
                </AxisCells>
            </StickyHeader>

            {allResources.map(resource => (
                <WorkPlannerSwimlane
                    key={resource.id}
                    resource={resource}
                    isEligible={eligibleResourceIds.has(resource.id)}
                    currentOpIds={currentOpIds}
                    onSelectOp={onSelectOp}
                    onInfo={handleInfo}
                    slots={slots}
                    bookings={bookings}
                    earliestStart={earliestStart}
                    shiftStart={shiftStart}
                    shiftEnd={shiftEnd}
                    slotMinutes={slotMinutes}
                    snapPreview={snapPreview}
                    selectedOp={selectedOp}
                    effectiveShift={resourceEffectiveShifts?.[resource.id] ?? null}
                    fieldConfig={fieldConfig}
                    labelConfig={labelConfig}
                />
            ))}

            {infoOp && <WorkPlannerOpInfoModal op={infoOp} config={opInfoModalConfig} onClose={handleInfoClose} />}
        </BoardWrapper>
    )
}

export default WorkPlannerBoard
